/* dog-trainer.js — vanilla progressive enhancement for the AI Dog Trainer page.
 * Replaces the framework runtime from the React prototype:
 *   1. the interactive trainer chat (#sn-dt-chat)  — profile switch, topic → typed reply
 *   2. the FAQ accordion (single-open)
 * No dependencies. Self-contained. Safe to load with `defer`.
 *
 * The page renders a faithful static snapshot server-side; this script enhances it.
 * If the script never runs, the static chat shell + FAQ questions are still visible.
 */
(function () {
  'use strict';

  // <html lang> comes from request.locale.iso_code, so /de/ pages report "de".
  var SN_DE = typeof document !== 'undefined' && document.documentElement
    ? /^de/i.test(document.documentElement.getAttribute('lang') || '')
    : false;
  var T = function (en, de) { return SN_DE ? de : en; };

  // ---- Chat data (ported verbatim from DogTrainerChat.jsx) ------------------
  var PROFILES = [
    { id: 'alfie', name: 'Alfie', meta: '10 mo · adolescent spaniel', bg: '#CFFF90',
      lead: 'he’s a bouncy adolescent with energy to burn - drain some with a sniffy walk first, keep sessions short and upbeat, and out-reward the distractions' },
    { id: 'rosie', name: 'Rosie', meta: '3 yr · nervous rescue', bg: '#FFDDBF',
      lead: 'she’s a nervous rescue, so confidence is the real goal - go slow, keep everything low-pressure, and reward calm rather than drilling repetitions' },
    { id: 'biscuit', name: 'Biscuit', meta: '4 mo · new puppy', bg: '#E6FFC6',
      lead: 'he’s a baby puppy with a tiny attention span - think 2-minute bursts, very high-value treats, and lots of patience while everything is brand new' },
  ];

  var TOPICS = [
    { id: 'lead', label: 'Lead pulling', q: 'How do I stop the lead-pulling?',
      a: function (p) { return [
        'The golden rule with ' + p.name + ': pulling never gets him where he wants to go. Since ' + p.lead + '.',
        'Stop dead the moment the lead goes tight, wait for it to slacken, then move off - and reward heavily whenever he’s at your side. It feels slow for the first few walks, then it clicks. A front-clip harness buys you time while the habit changes.',
      ]; } },
    { id: 'recall', label: 'Recall', q: 'How do I build reliable recall?',
      a: function (p) { return [
        'Recall is just a really well-paid habit. For ' + p.name + ', ' + p.lead + '.',
        'Start indoors with zero distractions: say the word once, reward like you’ve never rewarded before when she comes. Build distance, then distraction, then location - slowly. Never call her to something she’ll hate (bath, lead-on-to-go-home), and never tell her off for a slow return, or you poison the word.',
      ]; } },
    { id: 'crate', label: 'Crate training', q: 'How do I get them to like the crate?',
      a: function (p) { return [
        'The whole game is making the crate the best place in the house - never a punishment. With ' + p.name + ', ' + p.lead + '.',
        'Feed meals in it door-open, scatter treats inside, build up from seconds to minutes with you nearby before you ever close the door. Keep it positive and gradual; if there’s whining, you’ve likely jumped a step - drop back and rebuild.',
      ]; } },
    { id: 'barking', label: 'Barking & jumping', q: 'How do I handle barking and jumping up?',
      a: function (p) { return [
        'Both are usually “look at me!” behaviours - and attention (even telling-off) rewards them. For ' + p.name + ', ' + p.lead + '.',
        'Reward the calm, four-on-the-floor version instead: turn away from a jump, only give attention when paws are down. For barking, work out the trigger (boredom, alert, frustration) and meet that need - a tired, occupied dog barks far less.',
      ]; } },
    { id: 'toilet', label: 'Toilet training', q: 'How do I toilet train fast?',
      a: function (p) { return [
        'Frequency and timing win this, not telling-off. With ' + p.name + ' - ' + p.lead + '.',
        'Out after every sleep, meal, play and roughly every hour; reward the instant he goes outside, calmly clean up accidents with no fuss. Puppies physically can’t hold it long, so it’s on us to pre-empt. It comes together fast once the routine’s consistent.',
      ]; } },
    { id: 'separation', label: 'Being left alone', q: 'How do I help them cope with being left?',
      a: function (p) { return [
        'Build it like a muscle - tiny absences that stay under the point ' + p.name + ' starts to worry. Since ' + p.lead + '.',
        'Practise calm departures and returns (no big fuss either way), leave something to chew, and increase the time away in small steps. If there’s real panic - destruction, non-stop howling, toileting - that’s genuine separation anxiety and worth a behaviourist, not just more practice.',
      ]; } },
  ];

  // ---- German overrides ----------------------------------------------------
  // PROFILES/TOPICS are function-scoped vars; reassigning leaves the English
  // path untouched. Copy mirrors the German Liquid section (§13.7.1), including
  // the promoted `stubenrein` topic.
  if (SN_DE) {
    PROFILES = [
      { id: 'alfie', name: 'Alfie', meta: '10 Mon. · Spaniel in der Pubertät', bg: '#CFFF90',
        lead: 'er ist ein übermütiger Junghund mit viel Energie - laufen Sie erst eine Schnüffelrunde, halten Sie die Einheiten kurz und gut gelaunt, und seien Sie interessanter als die Ablenkung' },
      { id: 'rosie', name: 'Rosie', meta: '3 J. · ängstlicher Tierschutzhund', bg: '#FFDDBF',
        lead: 'sie ist ein ängstlicher Tierschutzhund, es geht also vor allem um Selbstvertrauen - langsam vorgehen, jeden Druck vermeiden und Ruhe belohnen statt Wiederholungen zu drillen' },
      { id: 'biscuit', name: 'Biscuit', meta: '4 Mon. · neuer Welpe', bg: '#E6FFC6',
        lead: 'er ist ein Welpe mit sehr kurzer Aufmerksamkeitsspanne - denken Sie in Zwei-Minuten-Einheiten, mit besonders guten Leckerlis und viel Geduld, solange alles noch neu ist' },
    ];
    TOPICS = [
      { id: 'lead', label: 'Leinenziehen', q: 'Wie gewöhne ich meinem Hund das Leinenziehen ab?',
        a: function (p) { return [
          'Die goldene Regel bei ' + p.name + ': Ziehen bringt ihn nie dorthin, wo er hinwill. Und ' + p.lead + '.',
          'Bleiben Sie stehen, sobald die Leine straff wird, warten Sie, bis sie durchhängt, und gehen Sie erst dann weiter - und belohnen Sie großzügig, wann immer er neben Ihnen läuft. Die ersten Spaziergänge fühlen sich zäh an, dann sitzt es. Ein Geschirr mit Front-Ring verschafft Ihnen Zeit, bis die Gewohnheit sich ändert.',
        ]; } },
      { id: 'recall', label: 'Rückruf', q: 'Wie baue ich einen zuverlässigen Rückruf auf?',
        a: function (p) { return [
          'Ein Rückruf ist nichts anderes als eine sehr gut bezahlte Gewohnheit. Bei ' + p.name + ' gilt: ' + p.lead + '.',
          'Fangen Sie drinnen ohne jede Ablenkung an: Das Wort einmal sagen und so belohnen wie noch nie, wenn er kommt. Dann Distanz steigern, dann Ablenkung, dann den Ort wechseln - langsam. Rufen Sie ihn nie zu etwas, das er nicht mag (Baden, Leine dran und heim), und schimpfen Sie nie, wenn er langsam kommt - sonst verbrennen Sie das Wort.',
        ]; } },
      { id: 'crate', label: 'Boxentraining', q: 'Wie gewöhne ich meinen Hund an die Box?',
        a: function (p) { return [
          'Es geht einzig darum, die Box zum besten Ort im Haus zu machen - nie zur Strafe. Bei ' + p.name + ': ' + p.lead + '.',
          'Füttern Sie bei offener Tür darin, streuen Sie Leckerlis hinein, steigern Sie von Sekunden auf Minuten, während Sie danebenbleiben - und schließen Sie die Tür erst viel später. Positiv und schrittweise. Wenn gejammert wird, war ein Schritt zu groß: zurückgehen und neu aufbauen.',
        ]; } },
      { id: 'barking', label: 'Bellen & Anspringen', q: 'Was tue ich gegen Bellen und Anspringen?',
        a: function (p) { return [
          'Beides heißt meist „schau mich an!“ - und Aufmerksamkeit belohnt es, auch Schimpfen. Bei ' + p.name + ': ' + p.lead + '.',
          'Belohnen Sie stattdessen die ruhige Variante mit allen vier Pfoten am Boden: Drehen Sie sich beim Anspringen weg und geben Sie erst Aufmerksamkeit, wenn die Pfoten unten sind. Beim Bellen finden Sie den Auslöser (Langeweile, Wachsamkeit, Frust) und decken das Bedürfnis - ein ausgelasteter Hund bellt deutlich weniger.',
        ]; } },
      { id: 'toilet', label: 'Stubenrein werden', q: 'Wie bekomme ich meinen Hund stubenrein?',
        a: function (p) { return [
          'Hier gewinnen Häufigkeit und Timing, nicht Schimpfen. Bei ' + p.name + ' - ' + p.lead + '.',
          'Raus nach jedem Schlafen, Fressen und Spielen und ungefähr stündlich; loben Sie in dem Moment, in dem er draußen löst, und beseitigen Sie Unfälle ruhig und kommentarlos. Welpen können körperlich noch nicht lange einhalten - es liegt an uns, ihnen zuvorzukommen. Mit einer festen Routine geht es schnell.',
        ]; } },
      { id: 'separation', label: 'Alleinbleiben', q: 'Wie lernt mein Hund, allein zu bleiben?',
        a: function (p) { return [
          'Bauen Sie es auf wie einen Muskel - winzige Abwesenheiten, die unter der Schwelle bleiben, ab der ' + p.name + ' unruhig wird. Und ' + p.lead + '.',
          'Üben Sie ruhiges Gehen und Wiederkommen (kein großes Theater in beide Richtungen), lassen Sie etwas zum Kauen da und steigern Sie die Zeit in kleinen Schritten. Bei echter Panik - Zerstörung, Dauerheulen, Lösen in der Wohnung - ist das Trennungsangst und ein Fall für eine Verhaltensberatung, nicht für mehr Übung.',
        ]; } },
    ];
  }

  function greeting(p) {
    return [{ from: 'trainer', paras: [
      T('Hi! I’m your dog trainer. I’ve got ', 'Hallo! Ich bin Ihr Hundetrainer. Ich habe das Profil von ') + p.name + T('’s profile open - ', ' offen - ') +
      p.meta.split('·')[1].trim() + T('. Tell me what you’re working on, or tap a topic below.', '. Sagen Sie mir, woran Sie arbeiten, oder tippen Sie unten ein Thema an.'),
    ] }];
  }

  // ---- SVG fragments (ported from the illustration components) --------------
  function orb(size) {
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 120 120" fill="none" aria-hidden="true">' +
      '<path d="M24 44C12 40 10 64 20 78c8 10 18 4 18-8Z" fill="#224031"></path>' +
      '<path d="M96 44c12-4 14 20 4 34-8 10-18 4-18-8Z" fill="#224031"></path>' +
      '<path d="M26 50c-6-2-7 12-2 20" stroke="#CFFF90" stroke-width="3" fill="none" opacity="0.6"></path>' +
      '<path d="M94 50c6-2 7 12 2 20" stroke="#CFFF90" stroke-width="3" fill="none" opacity="0.6"></path>' +
      '<circle cx="60" cy="60" r="46" fill="#224031"></circle>' +
      '<circle cx="60" cy="60" r="46" fill="none" stroke="#CFFF90" stroke-width="3" stroke-dasharray="6 9" opacity="0.7"></circle>' +
      '<circle cx="47" cy="55" r="6.5" fill="#CFFF90"></circle><circle cx="73" cy="55" r="6.5" fill="#CFFF90"></circle>' +
      '<circle cx="47" cy="57" r="2.5" fill="#224031"></circle><circle cx="73" cy="57" r="2.5" fill="#224031"></circle>' +
      '<ellipse cx="60" cy="70" rx="7" ry="5" fill="#CFFF90"></ellipse>' +
      '<path d="M60 75c0 5-8 7-12 3M60 75c0 5 8 7 12 3" stroke="#CFFF90" stroke-width="3.5" stroke-linecap="round" fill="none"></path>' +
      '<path d="M56 80c0 4 8 4 8 0Z" fill="#CFFF90" opacity="0.85"></path>' +
      '<path d="M96 28l2 6 6 2-6 2-2 6-2-6-6-2 6-2Z" fill="#CFFF90"></path></svg>';
  }
  function avatar(size, bg) {
    var ink = '#224031';
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 48 48" fill="none" aria-hidden="true">' +
      '<circle cx="24" cy="24" r="24" fill="' + bg + '"></circle>' +
      '<path d="M11 16c-4 0-5 9-1 14 2-4 4-7 6-9Z" fill="' + ink + '"></path>' +
      '<path d="M37 16c4 0 5 9 1 14-2-4-4-7-6-9Z" fill="' + ink + '"></path>' +
      '<circle cx="24" cy="25" r="12" fill="' + ink + '"></circle>' +
      '<circle cx="20" cy="23" r="2" fill="' + bg + '"></circle><circle cx="28" cy="23" r="2" fill="' + bg + '"></circle>' +
      '<ellipse cx="24" cy="29" rx="3" ry="2.2" fill="' + bg + '"></ellipse>' +
      '<path d="M24 31v3" stroke="' + bg + '" stroke-width="1.4" stroke-linecap="round"></path></svg>';
  }
  function icon(name, size, color) {
    var paths = {
      arrow: '<path d="M5 12h14M13 6l6 6-6 6"></path>',
      sparkle: '<path d="M12 3l1.8 4.9L18.7 9.7l-4.9 1.8L12 16.4l-1.8-4.9L5.3 9.7l4.9-1.8L12 3Z"></path>',
    };
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="' + color +
      '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + paths[name] + '</svg>';
  }

  // ---- Tiny DOM helper ------------------------------------------------------
  function h(tag, opts, kids) {
    var e = document.createElement(tag);
    opts = opts || {};
    if (opts.css) e.style.cssText = opts.css;
    if (opts.cls) e.className = opts.cls;
    if (opts.html != null) e.innerHTML = opts.html;
    if (opts.on) for (var ev in opts.on) e.addEventListener(ev, opts.on[ev]);
    if (opts.attrs) for (var a in opts.attrs) e.setAttribute(a, opts.attrs[a]);
    (kids || []).forEach(function (c) {
      if (c == null) return;
      e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return e;
  }

  // ---- Chat widget ----------------------------------------------------------
  function initChat(root) {
    var state = { profileId: 'alfie', messages: greeting(PROFILES[0]), typing: false, asked: [] };
    var typingTimer = null;
    var profile = function () { return PROFILES.filter(function (p) { return p.id === state.profileId; })[0]; };

    function bubble(msg, animate) {
      var isUser = msg.from === 'user';
      var inner = h('div', {
        css: 'max-width:78%;border-radius:18px;padding:12px 16px;font-size:14.5px;line-height:1.55;' +
          'background:' + (isUser ? 'var(--sn-forest)' : '#fff') + ';' +
          'color:' + (isUser ? 'var(--sn-cream)' : 'var(--sn-ink)') + ';' +
          'border:' + (isUser ? 'none' : '1px solid var(--sn-line)') + ';' +
          'border-bottom-right-radius:' + (isUser ? '5px' : '18px') + ';' +
          'border-bottom-left-radius:' + (isUser ? '18px' : '5px') + ';' +
          'display:flex;flex-direction:column;gap:9px',
      }, msg.typing
        ? [h('span', { css: 'display:inline-flex;gap:5px;color:var(--sn-ink-60);padding:2px 0',
            html: '<span class="sn-dot"></span><span class="sn-dot"></span><span class="sn-dot"></span>' })]
        : msg.paras.map(function (t) { return h('span', {}, [t]); }));

      return h('div', {
        cls: animate ? 'sn-msg-in' : '',
        css: 'display:flex;justify-content:' + (isUser ? 'flex-end' : 'flex-start') + ';gap:10px',
      }, [
        isUser ? null : h('div', { css: 'flex-shrink:0;margin-top:2px', html: orb(30) }),
        inner,
      ]);
    }

    function renderMessages(scroll) {
      var box = root.querySelector('[data-dt-messages]');
      box.innerHTML = '';
      var items = state.messages.slice();
      if (state.typing) items.push({ from: 'trainer', typing: true });
      items.forEach(function (m, i) { box.appendChild(bubble(m, i === items.length - 1)); });
      if (scroll !== false) box.scrollTop = box.scrollHeight;
    }

    function ask(topic) {
      if (state.typing) return;
      state.messages.push({ from: 'user', paras: [topic.q] });
      if (state.asked.indexOf(topic.id) === -1) state.asked.push(topic.id);
      state.typing = true;
      syncChips();
      renderMessages();
      clearTimeout(typingTimer);
      typingTimer = setTimeout(function () {
        state.messages.push({ from: 'trainer', paras: topic.a(profile()) });
        state.typing = false;
        syncChips(); // re-enable chips after reply (React does this via disabled={typing})
        renderMessages();
      }, 850);
    }

    function switchProfile(id) {
      if (id === state.profileId) return;
      clearTimeout(typingTimer);
      state.typing = false;
      state.asked = [];
      state.profileId = id;
      state.messages = greeting(PROFILES.filter(function (p) { return p.id === id; })[0]);
      syncProfile();
      syncChips();
      renderMessages();
    }

    function syncChips() {
      root.querySelectorAll('[data-dt-topic]').forEach(function (btn) {
        var id = btn.getAttribute('data-dt-topic');
        btn.style.background = state.asked.indexOf(id) !== -1 ? 'var(--sn-lime-soft)' : '#fff';
        btn.style.opacity = state.typing ? '0.5' : '1';
        btn.style.cursor = state.typing ? 'default' : 'pointer';
        btn.disabled = state.typing;
      });
    }

    function syncProfile() {
      var p = profile();
      root.querySelectorAll('[data-dt-profile]').forEach(function (btn) {
        var active = btn.getAttribute('data-dt-profile') === state.profileId;
        btn.style.border = active ? '2px solid var(--sn-forest)' : '2px solid var(--sn-line)';
        btn.style.background = active ? 'var(--sn-lime-soft)' : '#fff';
        btn.style.boxShadow = active ? '0 0 0 4px rgba(207,255,144,0.35)' : 'none';
      });
      var lbl = root.querySelector('[data-dt-training]');
      if (lbl) lbl.textContent = 'Training ' + p.name;
      var ph = root.querySelector('[data-dt-placeholder]');
      if (ph) ph.textContent = 'Ask anything about training ' + p.name + '…';
      var build = root.querySelector('[data-dt-build]');
      if (build) build.textContent = 'Build ' + p.name + '’s profile';
    }

    // Build the interactive widget (replaces the static SSR snapshot).
    var p0 = profile();
    var header = h('div', { css: 'display:flex;align-items:center;gap:12px;padding:16px 22px;border-bottom:1px solid var(--sn-line)' }, [
      h('div', { css: 'flex-shrink:0', html: orb(40) }),
      h('div', { css: 'flex:1' }, [
        h('div', { css: 'font-family:var(--sn-display);font-weight:700;font-size:16px' }, ['Dog Trainer']),
        h('div', { css: 'font-size:12.5px;color:var(--sn-ink-60);display:flex;align-items:center;gap:6px' }, [
          h('span', { css: 'width:7px;height:7px;border-radius:999px;background:#5B8C3E' }),
          h('span', { attrs: { 'data-dt-training': '' } }, ['Training ' + p0.name]),
        ]),
      ]),
      h('span', { cls: 'sn-eyebrow', css: 'color:var(--sn-forest);font-size:11px', html: icon('sparkle', 14, 'var(--sn-forest)') + ' AI' }),
    ]);

    var messages = h('div', { attrs: { 'data-dt-messages': '' }, css: 'flex:1;overflow-y:auto;padding:22px;display:flex;flex-direction:column;gap:14px' });

    var chips = h('div', { css: 'padding:12px 16px;border-top:1px solid var(--sn-line);display:flex;flex-wrap:wrap;gap:8px;background:#fff' },
      TOPICS.map(function (t) {
        return h('button', {
          attrs: { 'data-dt-topic': t.id },
          css: 'cursor:pointer;border-radius:999px;padding:8px 14px;font-size:13px;font-weight:600;font-family:var(--sn-display);border:1.5px solid var(--sn-line);background:#fff;color:var(--sn-forest);opacity:1',
          on: { click: function () { ask(t); } },
        }, [t.label]);
      }));

    var inputRow = h('div', { css: 'padding:14px 16px;border-top:1px solid var(--sn-line);display:flex;gap:10px;align-items:center;background:#fff' }, [
      h('div', { attrs: { 'data-dt-placeholder': '' }, css: 'flex:1;background:var(--sn-cream);border-radius:999px;padding:12px 18px;font-size:14px;color:var(--sn-ink-60);border:1px solid var(--sn-line)' }, ['Ask anything about training ' + p0.name + '…']),
      h('button', { css: 'border:none;cursor:pointer;width:44px;height:44px;border-radius:999px;background:var(--sn-forest);display:inline-flex;align-items:center;justify-content:center;flex-shrink:0', html: icon('arrow', 20, 'var(--sn-cream)') }),
    ]);

    var leftCol = h('div', { css: 'display:flex;flex-direction:column;height:600px;border-right:1px solid var(--sn-line)' }, [header, messages, chips, inputRow]);

    var chipsPanel = h('div', { css: 'display:flex;flex-direction:column;gap:10px' },
      PROFILES.map(function (p) {
        var active = p.id === state.profileId;
        return h('button', {
          attrs: { 'data-dt-profile': p.id },
          css: 'cursor:pointer;text-align:left;display:flex;align-items:center;gap:12px;border-radius:16px;padding:12px 14px;' +
            'border:' + (active ? '2px solid var(--sn-forest)' : '2px solid var(--sn-line)') + ';' +
            'background:' + (active ? 'var(--sn-lime-soft)' : '#fff') + ';' +
            'box-shadow:' + (active ? '0 0 0 4px rgba(207,255,144,0.35)' : 'none'),
          on: { click: function () { switchProfile(p.id); } },
        }, [
          h('div', { css: 'flex-shrink:0', html: avatar(42, p.bg) }),
          h('div', {}, [
            h('div', { css: 'font-family:var(--sn-display);font-weight:700;font-size:15.5px' }, [p.name]),
            h('div', { css: 'font-size:12.5px;color:var(--sn-ink-60)' }, [p.meta]),
          ]),
        ]);
      }));

    var rightCol = h('div', { css: 'padding:26px 26px;background:#fff;display:flex;flex-direction:column' }, [
      h('h3', { css: 'font-size:19px;margin-bottom:6px' }, ['Who are we training?']),
      h('p', { css: 'font-size:13.5px;color:var(--sn-ink-60);line-height:1.5;margin-bottom:18px' }, ['A nervous rescue, a bouncy adolescent and a brand-new puppy need different approaches. Pick one - watch the advice change.']),
      chipsPanel,
      h('div', { css: 'margin-top:auto;padding-top:22px' }, [
        h('div', { css: 'background:var(--sn-cream-2);border-radius:16px;padding:14px 16px;font-size:13px;color:var(--sn-ink-60);line-height:1.5' }, ['Your real trainer uses your dog’s breed, age and history from their profile.']),
        h('a', { cls: 'sn-btn sn-btn--primary', css: 'width:100%;justify-content:center;margin-top:12px' }, [
          h('span', { attrs: { 'data-dt-build': '' } }, ['Build ' + p0.name + '’s profile']),
          h('span', { css: 'display:inline-flex', html: icon('arrow', 18, 'currentColor') }),
        ]),
      ]),
    ]);

    var grid = h('div', { css: 'display:grid;grid-template-columns:1.5fr 1fr;border-radius:28px;overflow:hidden;border:1px solid var(--sn-line);box-shadow:0 30px 70px -45px rgba(28,25,23,0.5);background:#fff' }, [leftCol, rightCol]);

    root.innerHTML = '';
    root.appendChild(grid);
    renderMessages(false);
  }

  // The FAQ accordion is handled by the shared converted/_assets/sn-accordion.js.
  // Answers are rendered into the static body.html (for SEO/crawlability), so this
  // file only owns the chat widget.

  // ---- Boot -----------------------------------------------------------------
  function boot() {
    var chat = document.querySelector('[data-sn-widget="DogTrainerChat"]');
    if (chat) initChat(chat);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
